/**
 * What the platform says of a unit the shopper holds, the same on every store (the rule is in
 * `storefrontCopy/shared.ts`): whether it can be sold back, what selling it credits, and why it
 * can't be shipped right now. The storefront's inventory and the dashboard's theme preview both
 * read it. Withdrawing a unit is not here — an order reserves real stock, so it stays the tenant's
 * own server's call.
 */
import type { InventoryItem } from "./index.js";
import { capitalizeTerm, type StorefrontTerminology } from "./storefrontTerminology.js";

/**
 * A unit can be sold back when the shopper still owns it and it is worth something. The two
 * facts are on the row, so this reads them rather than trusting a flag the server would have to
 * keep in step. A worthless unit is withdraw-only, boxed's rule.
 */
export function isSellable(item: Pick<InventoryItem, "status" | "item">): boolean {
  return item.status === "owned" && item.item.value > 0;
}

/** The short status on a unit's tile that opens {@link withdrawalIssueText}'s sentence. */
export const WITHDRAWAL_ISSUE_LABEL = "Can't ship";

/**
 * Per cause: why the unit cannot be shipped, and whether waiting can change that. The sentence
 * the shopper reads is built from these in {@link withdrawalIssueText}.
 */
const WITHDRAWAL_ISSUE_TEXT: Partial<
  Record<
    NonNullable<InventoryItem["withdrawalIssue"]>,
    { reason: string | ((terms: StorefrontTerminology) => string); wait?: string }
  >
> = {
  voucher: {
    reason: (terms) => `${capitalizeTerm(terms.voucher.plural)} have nothing to ship.`,
  },
  listing_unavailable: { reason: "The seller no longer has this, so it cannot be shipped." },
  price_drift: {
    reason: "This costs more than it did, so it cannot be shipped right now.",
    wait: "wait for the price to come down",
  },
  catalog_out_of_stock: {
    reason: "Out of stock, so it cannot be shipped right now.",
    wait: "wait for a restock",
  },
  catalog_unfulfillable: { reason: "This can no longer be shipped." },
};

/**
 * What an owned unit's `withdrawalIssue` means for the shopper, or null when there is nothing to
 * say. A unit that can be sold ends with the sale, because selling is the one action left; a
 * worthless unit cannot be sold (`isSellable`), so its line offers only the wait, if any. The
 * lines differ in whether waiting helps, which is what the shopper decides on.
 */
export function withdrawalIssueText(
  item: Pick<InventoryItem, "status" | "item" | "withdrawalIssue">,
  terms: StorefrontTerminology,
): string | null {
  if (item.status !== "owned" || !item.withdrawalIssue) return null;
  const text = WITHDRAWAL_ISSUE_TEXT[item.withdrawalIssue];
  if (!text) return null;
  const reason = typeof text.reason === "function" ? text.reason(terms) : text.reason;
  if (isSellable(item)) {
    return `${reason} Sell it for its value${text.wait ? `, or ${text.wait}` : ""}.`;
  }
  if (!text.wait) return reason;
  return `${reason} ${capitalizeTerm(text.wait)}.`;
}

/** Σ value of the units named, which is what selling them credits. */
export function sellBackCents(items: readonly Pick<InventoryItem, "item">[]): number {
  return items.reduce((sum, item) => sum + item.item.value, 0);
}
